// CompanyQueriesForm.jsx
import { AiOutlineLoading } from "react-icons/ai";
import {
  Alert,
  Badge,
  Button,
  TextInput,
  Modal,
  Table,
  Spinner,
} from "flowbite-react";
import {
  FormProvider,
  useForm,
  Controller,
  useFormContext,
} from "react-hook-form";
import { ADMIN, AUDIT } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatToReal } from "../../utils/utils";
import { HiChevronDown, HiInformationCircle } from "react-icons/hi";
import { useQuery, useQueryClient } from "react-query";
import DrawerLoading from "../DrawerLoading";
import CurrencyInput from "../inputs/CurrencyInput";
import SelectInput from "../inputs/SelectInput";
import CustomSwitch from "../inputs/CustomSwitch";
import AuditHistoryModal from "../modals/AuditHistoryModal";
import { MdHistory } from "react-icons/md";
import { useApplication } from "../../contexts/ApplicationContext";

const SectionTitle = ({ children, right }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100">
      {children}
    </h3>
    <div className="flex items-center gap-2">{right}</div>
  </div>
);

const StatChip = ({ label, value }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
      {value}
    </span>
  </div>
);

const TabButton = ({
  active,
  onClick,
  title,
  subtitle,
  count,
  tone = "emerald",
  disabled = false,
}) => {
  const toneMap = {
    emerald: {
      active: "from-emerald-500 to-teal-500",
      ring: "ring-emerald-200 dark:ring-emerald-900/50",
      text: "text-emerald-700 dark:text-emerald-200",
      badge: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30",
    },
    amber: {
      active: "from-amber-500 to-orange-500",
      ring: "ring-amber-200 dark:ring-amber-900/50",
      text: "text-amber-700 dark:text-amber-200",
      badge: "bg-amber-50 text-amber-700 dark:bg-amber-900/30",
    },
  };
  const palette = toneMap[tone] || toneMap.emerald;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "w-full text-left rounded-xl border px-4 py-3 transition-all",
        "flex items-center justify-between gap-4",
        disabled
          ? "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800"
          : "hover:shadow-sm",
        active
          ? `bg-gradient-to-r ${palette.active} text-white border-transparent shadow-md ring-2 ${palette.ring}`
          : "bg-white/80 dark:bg-gray-900/60 border-gray-200 dark:border-gray-700",
      ].join(" ")}
    >
      <div className="min-w-0">
        <div
          className={[
            "text-sm md:text-base font-semibold truncate",
            active ? "text-white" : palette.text,
          ].join(" ")}
        >
          {title}
        </div>
        <div
          className={[
            "text-xs truncate",
            active ? "text-white/85" : "text-gray-500 dark:text-gray-400",
          ].join(" ")}
        >
          {subtitle}
        </div>
      </div>

      <div
        className={[
          "shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold",
          active ? "bg-white/20 text-white" : palette.badge,
        ].join(" ")}
      >
        {count} ativas
      </div>
    </button>
  );
};

const CustomAccordionItem = ({
  title,
  badge,
  open,
  onToggle,
  children,
}) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/60 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-sm md:text-base font-semibold text-gray-800 dark:text-gray-100 truncate">
          {title}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {badge}
        <HiChevronDown
          className={[
            "h-4 w-4 text-gray-500 transition-transform",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </div>
    </button>
    {open && <div className="px-4 pb-4 pt-1">{children}</div>}
  </div>
);

const QueryRow = ({
  name,
  priceName,
  activeName,
  basePrice,
  cqpId,
  onOpenHistory,
  debounceMs = 1200,
}) => {
  const { control, watch, clearErrors, setError } = useFormContext();
  const isActive = !!watch(activeName);
  const typedPrice = Number(watch(priceName) ?? 0);
  const base = Number(basePrice || 0);

  // ---- debounce helper com flag de "isDebouncing"
  const useDebounce = (value, delay) => {
    const [debounced, setDebounced] = useState(value);
    const [isDebouncing, setIsDebouncing] = useState(false);

    useEffect(() => {
      setIsDebouncing(true);
      const id = setTimeout(() => {
        setDebounced(value);
        setIsDebouncing(false);
      }, delay);
      return () => clearTimeout(id);
    }, [value, delay]);

    return [debounced, isDebouncing];
  };

  const [price, isDebouncing] = useDebounce(typedPrice, debounceMs); // preço “estabilizado”
  const isInvalidDebounced = isActive && price < base;

  // margem em % com 2 casas
  const marginPct = base > 0 ? ((price - base) / base) * 100 : null;
  const formatPct = (n) => `${n.toFixed(2)}%`;

  const marginTone = !isActive
    ? "text-gray-400 border-gray-200 dark:border-gray-700"
    : marginPct === null
    ? "text-gray-400 border-gray-200 dark:border-gray-700"
    : marginPct < 10
    ? "text-orange-600 border-orange-200 dark:border-orange-700 bg-orange-50/60 dark:bg-red-900/20"
    : "text-emerald-600 border-emerald-200 dark:border-emerald-700 bg-emerald-50/60 dark:bg-emerald-900/20";

  // aplica/limpa erro apenas depois do debounce
  useEffect(() => {
    if (!isActive) {
      clearErrors(priceName);
      return;
    }
    if (isInvalidDebounced) {
      setError(priceName, {
        type: "min",
        message: `Preço não pode ser menor que o custo (${formatToReal(base)})`,
      });
    } else {
      clearErrors(priceName);
    }
  }, [isActive, isInvalidDebounced, priceName, base, setError, clearErrors]);

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 py-3 md:py-3 mb-2">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Controller
            name={activeName}
            control={control}
            render={({ field: { value, onChange } }) => (
              <CustomSwitch
                id={`switch-${activeName}`}
                checked={!!value}
                onChange={onChange}
                label={name}
                subtitle={`Preço de custo: ${formatToReal(base)}`}
                size="md"
                color="emerald"
              />
            )}
          />
        </div>

        {/* Input + Chip de Margem (%) + Histórico */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <CurrencyInput
              name={priceName}
              className={[
                "w-full",
                isInvalidDebounced
                  ? "ring-1 ring-red-500 border-red-500 rounded-md"
                  : "",
              ].join(" ")}
              label="Preço"
              control={control}
              disabled={!isActive}
            />
          </div>

          {/* Chip de margem ou spinner */}
          <div
            className={[
              "shrink-0 px-2.5 py-1.5 rounded-full text-2xs font-semibold border flex items-center justify-center min-w-[52px] h-8",
              marginTone,
            ].join(" ")}
            title={
              marginPct === null
                ? "Sem custo base para calcular"
                : `Margem sobre o custo (${formatToReal(base)})`
            }
          >
            {isDebouncing ? (
              <AiOutlineLoading className="h-3.5 w-3.5 animate-spin" />
            ) : marginPct === null ? (
              "—%"
            ) : (
              <>Margem: {formatPct(marginPct)}</>
            )}
          </div>

          <Button
            color="light"
            size="xs"
            onClick={onOpenHistory}
            title="Ver histórico de alterações desta permissão"
            className="shrink-0"
            disabled={!cqpId}
          >
            <MdHistory fontSize={16} />
          </Button>
        </div>
      </div>

      {/* mensagens abaixo do input (após debounce) */}
      <div className="mt-1">
        {isInvalidDebounced ? (
          <div className="text-[10px] text-red-600">
            Preço não pode ser menor que o custo ({formatToReal(base)}).
          </div>
        ) : !isActive ? (
          <div className="text-[11px] text-gray-500 -mt-2">
            Ative para editar o preço
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ===========================
 * Main component
 * =========================== */
const CompanyQueriesForm = ({
  categories,
  company,
  onClose,
  fetchCompanies,
}) => {
  const { user } = useApplication();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setErrorState] = useState(null);
  const [detachedFromTable, setDetachedFromTable] = useState(false);
  const [searchTerms, setSearchTerms] = useState({});
  const applyingTableValues = useRef(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyCtx, setHistoryCtx] = useState({ cqpId: null, title: "" });
  const [activeTab, setActiveTab] = useState("main");
  const initialSnapshotRef = useRef({}); // { [QueryId]: { cqpId, isActive, price } }
  const [openMainPanels, setOpenMainPanels] = useState({});
  const [openAddPanels, setOpenAddPanels] = useState({});
  const queryClient = useQueryClient();

  // Estados independentes por categoria: "Mostrar só ativas"
  const [showOnlyActiveMap, setShowOnlyActiveMap] = useState({});
  const toggleOnlyActive = (key) =>
    setShowOnlyActiveMap((prev) => ({ ...prev, [key]: !prev[key] }));

  const sortByName = (arr) =>
    [...arr].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "pt", {
        sensitivity: "base",
      })
    );

  const methods = useForm({
    defaultValues: {
      company: {
        hasSurvey: false,
        hasCV: false,
        priceSurvey: 10,
        priceCV: 10,
        priceCVQuod: 10,
        priceCVBvs: 10,
        hasClientMonitoring: false,
        priceClientMonitoring: "0.00",
        vcm: "",
      },
      tableId: "",
      categories: [],
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = methods;

  const tableId = watch("tableId");
  const hasCV = watch("company.hasCV");
  const hasClientMonitoring = watch("company.hasClientMonitoring");
  const isRepresentativeUser =
    user?.Company?.companyTypeId === "REPRESENTANTE";
  const hideNegativacaoSection =
    isRepresentativeUser && !user?.Company?.hasCV;

  /* ===========================
   * Data fetching
   * =========================== */
  const { isLoading: isLoadingCompanyQueries, data: companyQueries } = useQuery(
    ["companyQueries", company?.id],
    async () => {
      const { data: items } = await axios.get(
        `${ADMIN.GET_COMPANY_QUERIES}/${company?.id}`
      );
      return items.data;
    },
    { enabled: !!company?.id }
  );

  const { isLoading: isLoadingTables, data: tables } = useQuery(
    ["tables"],
    async () => {
      const { data } = await axios.get(`${ADMIN.GET_TABLES}?itemsPerPage=-1`);
      return data.data.data;
    },
    { enabled: !!company }
  );

  const { isFetching: isFetchingTableQueries, data: tableQueriesPkg } =
    useQuery(
      ["tableQueries", tableId],
      async () => {
        const currentId = tableId;
        if (!currentId) return null;
        const { data: items } = await axios.get(
          `${ADMIN.GET_TABLE_QUERIES}/${currentId}`
        );
        return { tableId: currentId, data: items.data };
      },
      { enabled: !!tableId }
    );

  /* ===========================
   * Effects: defaults e sincronização
   * =========================== */
  useEffect(() => {
    if (!company || !companyQueries || !categories) return;

    const formDefaults = {
      tableId: company?.tableId || "",
      company: {
        hasSurvey: !!company.hasSurvey,
        hasCV: !!company.hasCV,
        priceSurvey: company.priceSurvey ?? 10,
        priceCV: company.priceCV ?? 10,
        priceCVQuod: company.priceCVQuod ?? company.priceCV ?? 10,
        priceCVBvs: company.priceCVBvs ?? company.priceCV ?? 10,
        hasClientMonitoring: !!company.hasClientMonitoring,
        priceClientMonitoring: company.priceClientMonitoring ?? "0.00",
        vcm: company.vcm || "",
      },
      categories: categories.map((category) => ({
        queries: category.Queries.map((query) => {
          const matched = companyQueries.find((cq) => cq.QueryId === query.id);
          return {
            QueryId: query.id,
            isActive: !!matched,
            price: matched?.price ?? query.price,
            cqpId: matched?.cqpId || null,
          };
        }),
      })),
    };

    reset(formDefaults);

    // monta snapshot inicial para cálculo de diffs
    const snap = {};
    formDefaults.categories.forEach((cat) => {
      cat.queries.forEach((q) => {
        snap[q.QueryId] = {
          cqpId: q.cqpId,
          isActive: q.isActive,
          price: Number(q.price ?? 0),
        };
      });
    });
    initialSnapshotRef.current = snap;

    applyingTableValues.current = true;
    setValue("company.hasSurvey", !!company?.hasSurvey, { shouldDirty: false });
    setValue("company.hasCV", !!company?.hasCV, { shouldDirty: false });
    setValue("company.priceSurvey", company?.priceSurvey ?? 10, {
      shouldDirty: false,
    });
    setValue("company.priceCV", company?.priceCV ?? 10, { shouldDirty: false });
    setValue("company.priceCVQuod", company?.priceCVQuod ?? 10, {
      shouldDirty: false,
    });
    setValue("company.priceCVBvs", company?.priceCVBvs ?? 10, {
      shouldDirty: false,
    });
    setValue("company.hasClientMonitoring", !!company?.hasClientMonitoring, {
      shouldDirty: false,
    });
    setValue(
      "company.priceClientMonitoring",
      company?.priceClientMonitoring ?? "0.00",
      { shouldDirty: false }
    );
    setValue("tableId", company?.tableId || "", { shouldDirty: false });
    setDetachedFromTable(false);

    queueMicrotask(() => {
      applyingTableValues.current = false;
    });
  }, [company, companyQueries, categories, reset, setValue]);

  useEffect(() => {
    if (!tables || !tableId) return;
    const selectedTable = tables.find((t) => String(t.id) === String(tableId));
    if (!selectedTable) return;

    applyingTableValues.current = true;
    setValue("company.vcm", selectedTable.vcm || "", { shouldDirty: false });
    setValue("company.hasSurvey", !!selectedTable.hasSurvey, {
      shouldDirty: false,
    });
    setValue("company.hasCV", !!selectedTable.hasCV, { shouldDirty: false });
    setValue("company.priceSurvey", selectedTable.priceSurvey ?? 10, {
      shouldDirty: false,
    });
    setValue("company.priceCV", selectedTable.priceCV ?? 10, {
      shouldDirty: false,
    });

    queueMicrotask(() => {
      applyingTableValues.current = false;
    });
  }, [tableId, tables, setValue]);

  useEffect(() => {
    if (
      !tableId ||
      !tableQueriesPkg?.data ||
      tableQueriesPkg.tableId !== tableId ||
      !categories
    )
      return;

    applyingTableValues.current = true;
    const tableQueries = tableQueriesPkg.data;
    categories.forEach((category, idxCat) => {
      category.Queries.forEach((query, idxQuery) => {
        const matched = tableQueries.find((tq) => tq.QueryId === query.id);
        setValue(
          `categories[${idxCat}].queries[${idxQuery}].isActive`,
          !!matched,
          { shouldDirty: false }
        );
        setValue(
          `categories[${idxCat}].queries[${idxQuery}].price`,
          matched?.price ?? query.price,
          { shouldDirty: false }
        );
      });
    });

    queueMicrotask(() => {
      applyingTableValues.current = false;
    });
  }, [tableId, tableQueriesPkg, categories, setValue]);

  // Desacoplar da tabela em edição manual
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      const currentTableId = watch("tableId");
      if (!currentTableId) return;
      if (applyingTableValues.current) return;
      if (!isDirty) return;

      const isCompanyField =
        name === "company.priceSurvey" ||
        name === "company.priceCV" ||
        name === "company.priceCVQuod" ||
        name === "company.priceCVBvs";

      const isQueryPrice = /^categories\[\d+\]\.queries\[\d+\]\.price$/.test(
        name || ""
      );
      const isQueryActive =
        /^categories\[\d+\]\.queries\[\d+\]\.isActive$/.test(name || "");

      if (isCompanyField || isQueryPrice || isQueryActive) {
        setValue("tableId", "", { shouldDirty: false });
        setDetachedFromTable(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, isDirty, setValue]);

  const openHistory = (cqpId, title) => {
    if (!cqpId) return;
    setHistoryCtx({ cqpId, title });
    setHistoryOpen(true);
  };

  /* ===========================
   * Helpers & flags
   * =========================== */
  const watchedCategories = watch("categories", []);

  const activeCountsByCategory = useMemo(() => {
    return (watchedCategories || []).map((cat) => {
      const qs = cat?.queries || [];
      return qs.reduce((acc, q) => acc + (q?.isActive ? 1 : 0), 0);
    });
  }, [watchedCategories]);

  const totalActive = useMemo(
    () => activeCountsByCategory.reduce((sum, n) => sum + n, 0),
    [activeCountsByCategory]
  );

  const tabActiveCounts = useMemo(() => {
    let main = 0;
    let add = 0;
    categories?.forEach((category, idxCat) => {
      category?.Queries?.forEach((query, idxQuery) => {
        const isActive =
          watchedCategories?.[idxCat]?.queries?.[idxQuery]?.isActive ?? false;
        if (!isActive) return;
        if (query?.isAdditional) {
          add += 1;
        } else {
          main += 1;
        }
      });
    });
    return { main, add };
  }, [categories, watchedCategories]);

  const hasMainQueries = useMemo(
    () =>
      categories?.some((category) =>
        category?.Queries?.some((query) => !query.isAdditional)
      ),
    [categories]
  );

  const hasAdditionalQueries = useMemo(
    () =>
      categories?.some((category) =>
        category?.Queries?.some((query) => !!query.isAdditional)
      ),
    [categories]
  );

  const hasInvalidPrice = useMemo(() => {
    try {
      return categories?.some((category, idxCat) =>
        category?.Queries?.some((query, idxQuery) => {
          const isActive = !!watch(
            `categories[${idxCat}].queries[${idxQuery}].isActive`
          );
          const priceVal = Number(
            watch(`categories[${idxCat}].queries[${idxQuery}].price`) ?? 0
          );
          return isActive && priceVal < Number(query.price ?? 0);
        })
      );
    } catch {
      return false;
    }
  }, [categories, watch]);

  const toggleAllInCategory = (idxCat, queryIndices, value) => {
    queryIndices.forEach((idxQuery) => {
      setValue(`categories[${idxCat}].queries[${idxQuery}].isActive`, value, {
        shouldDirty: true,
      });
    });
  };

  const buildCurrentMap = (formData) => {
    const map = {};
    (formData?.categories || []).forEach((cat) => {
      (cat?.queries || []).forEach((q) => {
        map[q.QueryId] = {
          cqpId: q.cqpId ?? null,
          isActive: !!q.isActive,
          price: Number(q.price ?? 0),
        };
      });
    });
    return map;
  };

  /* ===========================
   * Submit
   * =========================== */
  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      // Flatten das queries
      const flat = [];
      if (Array.isArray(data.categories)) {
        data.categories.forEach((cat) => {
          if (Array.isArray(cat.queries)) {
            cat.queries.forEach((q) => flat.push(q));
          }
        });
      }

      data.queries = flat.filter((q) => q?.isActive);
      data.company.hasCV = !!data.company.hasCV;
      data.company.hasSurvey = !!data.company.hasSurvey;
      data.company.hasClientMonitoring = !!data.company.hasClientMonitoring;
      if (typeof data.company.priceClientMonitoring !== "undefined") {
        const value = data.company.priceClientMonitoring;
        data.company.priceClientMonitoring =
          value === "" || value == null
            ? null
            : Number(String(value).replace(",", "."));
      }

      // ⬇️ Normalização do tableId:
      // - Se usuário saiu da tabela (select vazio ou detachment automático), enviar null
      // - Só manda um número/ID se realmente tiver uma tabela selecionada
      if (!data.tableId || detachedFromTable) {
        data.tableId = null;
      }

      // 1) Salva no backend
      await axios.put(`${ADMIN.GET_COMPANY_QUERIES}/${company?.id}`, data);

      // 2) Calcula diffs de preço (somente UPDATE de preço com CQP existente e ainda ativo)
      const prevMap = initialSnapshotRef.current || {};
      const currMap = buildCurrentMap(data);

      const auditPayloads = [];
      for (const queryId of Object.keys(currMap)) {
        const prev = prevMap[queryId];
        const curr = currMap[queryId];
        if (!prev || !curr) continue;

        const priceChanged = Number(prev.price) !== Number(curr.price);
        if (curr.cqpId && curr.isActive && prev.isActive && priceChanged) {
          auditPayloads.push({
            action: "UPDATE",
            entityName: "CompanyQueryPermission",
            entityId: curr.cqpId,
            previousValues: { price: Number(prev.price) },
            newValues: { price: Number(curr.price) },
            changedFields: ["price"],
            reason: "Atualização de preço via painel",
            source: "admin_panel",
            companyId: company?.id,
          });
        }
      }

      if (auditPayloads.length) {
        await Promise.allSettled(
          auditPayloads.map((p) => axios.post(AUDIT.CREATE, p))
        );
      }

      // 3) Atualiza snapshot para refletir o novo estado salvo
      initialSnapshotRef.current = currMap;

      // 4) Refetch local + listagens externas
      queryClient.invalidateQueries(["companyQueries", company?.id]);
      if (tableId) {
        queryClient.invalidateQueries(["tableQueries", tableId]);
      }
      fetchCompanies?.();
      onClose?.(false);
    } catch (err) {
      setErrorState(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ===========================
   * Render
   * =========================== */
  return (
    <FormProvider {...methods}>
      <>
        {/* Header */}
        <div className="mb-4 -mt-1">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                Permissões de Consulta
              </h2>
              <p className="text-sm text-gray-500">
                Cliente:{" "}
                <span className="font-medium">
                  {company?.name || company?.socialReason}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-2">
              <StatChip label="Ativas" value={totalActive} />
              <StatChip
                label="Tabela"
                value={
                  tableId
                    ? tables?.find((t) => String(t.id) === String(tableId))
                        ?.name || `#${tableId}`
                    : "Sem tabela"
                }
              />
              {(() => {
                const sel = tables?.find(
                  (t) => String(t.id) === String(tableId)
                );
                return sel?.vcm ? (
                  <StatChip label="VCM" value={formatToReal(sel.vcm)} />
                ) : null;
              })()}
            </div>
          </div>
        </div>

        <form
          className="pb-28 md:pb-24"
          onSubmit={handleSubmit(onSubmit)}
          key={company?.id || "no-company"}
        >
          {/* Card: Seleção de Tabela */}
          <div className="mb-5">
            <SectionTitle
              right={
                detachedFromTable && company?.tableId ? (
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => {
                      setValue("tableId", company.tableId || "");
                      setDetachedFromTable(false);
                    }}
                  >
                    Voltar à tabela
                  </Button>
                ) : null
              }
            >
              Modelo de tabela
            </SectionTitle>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <SelectInput
                  key={`select-${company?.id || "none"}`}
                  register={register}
                  label="Tabela pré-definida"
                  name="tableId"
                  required={false}
                >
                  <option value="">Nenhum selecionado</option>
                  {tables?.map((table) => (
                    <option key={table.id} value={table.id}>
                      {table.name} —{" "}
                      {table.vcm ? formatToReal(table.vcm) : "Sem VCM"}
                    </option>
                  ))}
                </SelectInput>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-xs text-gray-500">
                    Se você alterar os preços/ativação manualmente, o cliente
                    deixará de seguir a tabela selecionada.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {detachedFromTable && (
            <Alert color="info" icon={HiInformationCircle} className="mb-4">
              Os valores foram alterados manualmente. Este cliente não faz mais
              parte de uma tabela pré-definida.
            </Alert>
          )}

          {/* Permissão de Negativação (CV) */}
          {!hideNegativacaoSection && (
            <div className="mb-6">
              <SectionTitle>Negativação</SectionTitle>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/60 p-4">
                <div className="space-y-4">
                  <Controller
                    name="company.hasCV"
                    control={methods.control}
                    render={({ field }) => (
                      <CustomSwitch
                        id="switch-company-hasCV"
                        checked={!!field.value}
                        onChange={(val) => field.onChange(!!val)}
                        label="Habilitar negativação para este cliente"
                        subtitle="Permite registrar e dar baixa em negativações."
                        size="md"
                        color="emerald"
                      />
                    )}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <CurrencyInput
                      name="company.priceCVQuod"
                      label="Valor negativação Quod"
                      control={methods.control}
                      disabled={!hasCV}
                      className={hasCV ? "" : "opacity-60"}
                    />
                    <CurrencyInput
                      name="company.priceCVBvs"
                      label="Valor negativação BVS (em breve)"
                      control={methods.control}
                      disabled={!hasCV}
                      className={hasCV ? "" : "opacity-60"}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <SectionTitle>Monitoramento de clientes</SectionTitle>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/60 p-4">
              <div className="space-y-4">
                <Controller
                  name="company.hasClientMonitoring"
                  control={methods.control}
                  render={({ field }) => (
                    <CustomSwitch
                      id="switch-company-hasClientMonitoring"
                      checked={!!field.value}
                      onChange={(val) => {
                        field.onChange(!!val);
                        if (!val) {
                          setValue("company.priceClientMonitoring", "0.00", {
                            shouldDirty: true,
                          });
                        }
                      }}
                      label="Habilitar Monitore Clientes para este cliente"
                      subtitle="Permite adicionar clientes para monitoramento."
                      size="md"
                      color="blue"
                    />
                  )}
                />

                <CurrencyInput
                  name="company.priceClientMonitoring"
                  label="Valor do monitoramento de clientes"
                  control={methods.control}
                  disabled={!hasClientMonitoring}
                  className={hasClientMonitoring ? "" : "opacity-60"}
                />
              </div>
            </div>
          </div>

          {/* Accordion de Categorias */}
          {categories?.length > 0 ? (
            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-white via-white to-emerald-50/40 dark:from-gray-900 dark:via-gray-900 dark:to-emerald-900/20 p-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <TabButton
                    active={activeTab === "main"}
                    onClick={() => setActiveTab("main")}
                    title="Consultas principais"
                    subtitle="Essenciais do contrato"
                    count={tabActiveCounts.main}
                    tone="emerald"
                    disabled={!hasMainQueries}
                  />
                  <TabButton
                    active={activeTab === "add"}
                    onClick={() => setActiveTab("add")}
                    title="Consultas adicionais"
                    subtitle="Extras sob demanda"
                    count={tabActiveCounts.add}
                    tone="amber"
                    disabled={!hasAdditionalQueries}
                  />
                </div>
              </div>

              {activeTab === "main" ? (
                <div>
                  <div className="w-full space-y-2">
                    {categories.map((category, idxCat) => {
                      const keyMain = `main-${idxCat}`;
                      const onlyActiveMain = !!showOnlyActiveMap[keyMain];
                      const term = (searchTerms[idxCat] ?? "").toLowerCase();
                      const isOpen = !!openMainPanels[keyMain];

                      // base: principais que batem o termo
                      let filtered = category.Queries.filter(
                        (q) =>
                          !q.isAdditional && q.name.toLowerCase().includes(term)
                      );

                      // se “só ativas” (por categoria) estiver ligado
                      if (onlyActiveMain) {
                        const watchedCat =
                          watchedCategories?.[idxCat]?.queries || [];
                        filtered = filtered.filter((q) => {
                          const realIndex = category.Queries.findIndex(
                            (qq) => qq.id === q.id
                          );
                          return !!watchedCat?.[realIndex]?.isActive;
                        });
                      }

                      // ordenar por nome
                      filtered = filtered.sort((a, b) =>
                        String(a.name || "").localeCompare(
                          String(b.name || ""),
                          "pt",
                          {
                            sensitivity: "base",
                          }
                        )
                      );

                      // contador de ativas considerando o conjunto filtrado
                      const watchedCat =
                        watchedCategories?.[idxCat]?.queries || [];
                      const activeCountMain = filtered.reduce((acc, q) => {
                        const idxReal = category.Queries.findIndex(
                          (qq) => qq.id === q.id
                        );
                        const isActive =
                          watchedCat?.[idxReal]?.isActive ?? false;
                        return acc + (isActive ? 1 : 0);
                      }, 0);

                      return (
                        <CustomAccordionItem
                          key={`main-${idxCat}`}
                          title={category.name}
                          open={isOpen}
                          onToggle={() =>
                            setOpenMainPanels((prev) => ({
                              ...prev,
                              [keyMain]: !prev[keyMain],
                            }))
                          }
                          badge={
                            <Badge color="info">
                              {activeCountMain} ativas
                            </Badge>
                          }
                        >
                          {category?.Queries?.some((q) => !q.isAdditional) ? (
                            <div className="flex flex-col gap-3">
                              {/* Barra de ações */}
                              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                                <div className="w-full md:w-80">
                                  <TextInput
                                    sizing="sm"
                                    placeholder="Filtrar consultas principais desta categoria..."
                                    value={searchTerms[idxCat] ?? ""}
                                    onChange={(e) =>
                                      setSearchTerms((prev) => ({
                                        ...prev,
                                        [idxCat]: e.target.value,
                                      }))
                                    }
                                  />
                                  <div className="mt-1 text-xs text-gray-500">
                                    Digite parte do nome para filtrar.
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  {(searchTerms[idxCat] ?? "").length > 0 && (
                                    <Button
                                      color="light"
                                      size="xs"
                                      onClick={() =>
                                        setSearchTerms((prev) => ({
                                          ...prev,
                                          [idxCat]: "",
                                        }))
                                      }
                                    >
                                      Limpar filtro
                                    </Button>
                                  )}

                                  {/* Botão "Só ativas" por categoria (PRINCIPAIS) */}
                                  <Button
                                    color={
                                      onlyActiveMain ? "success" : "light"
                                    }
                                    size="xs"
                                    onClick={() => toggleOnlyActive(keyMain)}
                                    title="Exibir somente consultas ativas desta categoria"
                                  >
                                    {onlyActiveMain
                                      ? "Mostrando só ativas"
                                      : "Mostrar só ativas"}
                                  </Button>

                                  <Button
                                    color="success"
                                    size="xs"
                                    onClick={() => {
                                      const indices = category.Queries.map(
                                        (q, i) => ({
                                          i,
                                          name: q.name.toLowerCase(),
                                          isAdditional: !!q.isAdditional,
                                        })
                                      )
                                        .filter(
                                          ({ name, isAdditional }) =>
                                            !isAdditional &&
                                            name.includes(term)
                                        )
                                        .map(({ i }) => i);
                                      toggleAllInCategory(
                                        idxCat,
                                        indices,
                                        true
                                      );
                                    }}
                                  >
                                    Selecionar Todas
                                  </Button>

                                  <Button
                                    color="failure"
                                    size="xs"
                                    onClick={() => {
                                      const indices = category.Queries.map(
                                        (q, i) => ({
                                          i,
                                          name: q.name.toLowerCase(),
                                          isAdditional: !!q.isAdditional,
                                        })
                                      )
                                        .filter(
                                          ({ name, isAdditional }) =>
                                            !isAdditional &&
                                            name.includes(term)
                                        )
                                        .map(({ i }) => i);
                                      toggleAllInCategory(
                                        idxCat,
                                        indices,
                                        false
                                      );
                                    }}
                                  >
                                    Limpar todas
                                  </Button>
                                </div>
                              </div>

                              {/* Lista filtrada (PRINCIPAIS) */}
                              <div className="flex flex-col">
                                {filtered.length > 0 ? (
                                  filtered.map((query) => {
                                    const realIndex =
                                      category.Queries.findIndex(
                                        (q) => q.id === query.id
                                      );
                                    const cqpId = watch(
                                      `categories[${idxCat}].queries[${realIndex}].cqpId`
                                    );
                                    return (
                                      <QueryRow
                                        key={`main-${query.id}-${realIndex}`}
                                        name={query.name}
                                        priceName={`categories[${idxCat}].queries[${realIndex}].price`}
                                        activeName={`categories[${idxCat}].queries[${realIndex}].isActive`}
                                        basePrice={query.price}
                                        cqpId={cqpId}
                                        onOpenHistory={() =>
                                          openHistory(cqpId, query.name)
                                        }
                                      />
                                    );
                                  })
                                ) : (
                                  <div className="text-sm md:text-[15px] text-center py-2 text-gray-500">
                                    Nenhuma consulta principal corresponde ao
                                    filtro.
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm md:text-[15px] text-center py-2 text-gray-500">
                              Não há consultas principais nesta categoria.
                            </div>
                          )}
                        </CustomAccordionItem>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="w-full space-y-2">
                    {categories.map((category, idxCat) => {
                      const keyAdd = `add-${idxCat}`;
                      const onlyActiveAdd = !!showOnlyActiveMap[keyAdd];
                      const term = (
                        searchTerms[`add-${idxCat}`] ?? ""
                      ).toLowerCase();
                      const isOpen = !!openAddPanels[keyAdd];

                      // base: adicionais que batem o termo
                      let filtered = category.Queries.filter(
                        (q) =>
                          !!q.isAdditional && q.name.toLowerCase().includes(term)
                      );

                      // se “só ativas” (por categoria) estiver ligado
                      if (onlyActiveAdd) {
                        const watchedCat =
                          watchedCategories?.[idxCat]?.queries || [];
                        filtered = filtered.filter((q) => {
                          const realIndex = category.Queries.findIndex(
                            (qq) => qq.id === q.id
                          );
                          return !!watchedCat?.[realIndex]?.isActive;
                        });
                      }

                      // ordenar por nome
                      filtered = filtered.sort((a, b) =>
                        String(a.name || "").localeCompare(
                          String(b.name || ""),
                          "pt",
                          {
                            sensitivity: "base",
                          }
                        )
                      );

                      // contador de ativas considerando o conjunto filtrado
                      const watchedCat =
                        watchedCategories?.[idxCat]?.queries || [];
                      const activeCountAdd = filtered.reduce((acc, q) => {
                        const idxReal = category.Queries.findIndex(
                          (qq) => qq.id === q.id
                        );
                        const isActive =
                          watchedCat?.[idxReal]?.isActive ?? false;
                        return acc + (isActive ? 1 : 0);
                      }, 0);

                      return (
                        <CustomAccordionItem
                          key={`add-${idxCat}`}
                          title={category.name}
                          open={isOpen}
                          onToggle={() =>
                            setOpenAddPanels((prev) => ({
                              ...prev,
                              [keyAdd]: !prev[keyAdd],
                            }))
                          }
                          badge={
                            <Badge color="purple">
                              {activeCountAdd} ativas
                            </Badge>
                          }
                        >
                          {category?.Queries?.some((q) => !!q.isAdditional) ? (
                            <div className="flex flex-col gap-3">
                              {/* Barra de ações (ADICIONAIS) */}
                              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                                <div className="w-full md:w-80">
                                  <TextInput
                                    sizing="sm"
                                    placeholder="Filtrar consultas adicionais desta categoria..."
                                    value={
                                      searchTerms[`add-${idxCat}`] ?? ""
                                    }
                                    onChange={(e) =>
                                      setSearchTerms((prev) => ({
                                        ...prev,
                                        [`add-${idxCat}`]: e.target.value,
                                      }))
                                    }
                                  />
                                  <div className="mt-1 text-xs text-gray-500">
                                    Digite parte do nome para filtrar.
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  {(searchTerms[`add-${idxCat}`] ?? "")
                                    .length > 0 && (
                                    <Button
                                      color="light"
                                      size="xs"
                                      onClick={() =>
                                        setSearchTerms((prev) => ({
                                          ...prev,
                                          [`add-${idxCat}`]: "",
                                        }))
                                      }
                                    >
                                      Limpar filtro
                                    </Button>
                                  )}

                                  {/* Botão "Só ativas" por categoria (ADICIONAIS) */}
                                  <Button
                                    color={
                                      onlyActiveAdd ? "success" : "light"
                                    }
                                    size="xs"
                                    onClick={() => toggleOnlyActive(keyAdd)}
                                    title="Exibir somente consultas ativas desta categoria"
                                  >
                                    {onlyActiveAdd
                                      ? "Mostrando só ativas"
                                      : "Mostrar só ativas"}
                                  </Button>

                                  <Button
                                    color="success"
                                    size="xs"
                                    onClick={() => {
                                      const indices = category.Queries.map(
                                        (q, i) => ({
                                          i,
                                          name: q.name.toLowerCase(),
                                          isAdditional: !!q.isAdditional,
                                        })
                                      )
                                        .filter(
                                          ({ name, isAdditional }) =>
                                            isAdditional &&
                                            name.includes(term)
                                        )
                                        .map(({ i }) => i);
                                      toggleAllInCategory(
                                        idxCat,
                                        indices,
                                        true
                                      );
                                    }}
                                  >
                                    Selecionar Todas
                                  </Button>

                                  <Button
                                    color="failure"
                                    size="xs"
                                    onClick={() => {
                                      const indices = category.Queries.map(
                                        (q, i) => ({
                                          i,
                                          name: q.name.toLowerCase(),
                                          isAdditional: !!q.isAdditional,
                                        })
                                      )
                                        .filter(
                                          ({ name, isAdditional }) =>
                                            isAdditional &&
                                            name.includes(term)
                                        )
                                        .map(({ i }) => i);
                                      toggleAllInCategory(
                                        idxCat,
                                        indices,
                                        false
                                      );
                                    }}
                                  >
                                    Limpar todas
                                  </Button>
                                </div>
                              </div>

                              {/* Lista filtrada (ADICIONAIS) */}
                              <div className="flex flex-col">
                                {filtered.length > 0 ? (
                                  filtered.map((query) => {
                                    const realIndex =
                                      category.Queries.findIndex(
                                        (q) => q.id === query.id
                                      );
                                    const cqpId = watch(
                                      `categories[${idxCat}].queries[${realIndex}].cqpId`
                                    );
                                    return (
                                      <QueryRow
                                        key={`add-${query.id}-${realIndex}`}
                                        name={query.name}
                                        priceName={`categories[${idxCat}].queries[${realIndex}].price`}
                                        activeName={`categories[${idxCat}].queries[${realIndex}].isActive`}
                                        basePrice={query.price}
                                        cqpId={cqpId}
                                        onOpenHistory={() =>
                                          openHistory(cqpId, query.name)
                                        }
                                      />
                                    );
                                  })
                                ) : (
                                  <div className="text-sm md:text-[15px] text-center py-2 text-gray-500">
                                    Nenhuma consulta adicional corresponde ao
                                    filtro.
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm md:text-[15px] text-center py-2 text-gray-500">
                              Não há consultas adicionais nesta categoria.
                            </div>
                          )}
                        </CustomAccordionItem>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm md:text-md text-center">
              Você não possui nenhuma permissão de consulta. Por favor, entre em
              contato com o administrador do sistema.
            </div>
          )}

          {/* Barra de ações (sticky) */}
          <div className="fixed left-0 right-0 bottom-0 md:bottom-2 z-30">
            <div className="mx-2 md:mx-auto md:max-w-3xl lg:max-w-2xl">
              <div className="backdrop-blur bg-white/90 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-700 shadow-lg rounded-xl p-3 md:p-4 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {tableId ? (
                    <>
                      Seguindo:{" "}
                      <span className="font-medium">
                        {tables?.find((t) => String(t.id) === String(tableId))
                          ?.name || `#${tableId}`}
                      </span>
                    </>
                  ) : (
                    <>Sem tabela vinculada</>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    color="light"
                    onClick={() => onClose?.(false)}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="primary"
                    isProcessing={isSubmitting}
                    processingSpinner={
                      <AiOutlineLoading className="h-6 w-6 animate-spin" />
                    }
                    disabled={isSubmitting || hasInvalidPrice}
                    title={
                      hasInvalidPrice
                        ? "Corrija os preços abaixo do custo para habilitar"
                        : undefined
                    }
                  >
                    Atualizar permissões
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <Alert color="failure" icon={HiInformationCircle} className="mt-4">
              Erro ao atualizar permissões do cliente
            </Alert>
          )}
        </form>

        {(isLoadingCompanyQueries ||
          isLoadingTables ||
          isFetchingTableQueries) && <DrawerLoading />}

        {/* Modal de Histórico */}
        <AuditHistoryModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          cqpId={historyCtx.cqpId}
          title={historyCtx.title}
          companyId={company?.id}
        />
      </>
    </FormProvider>
  );
};

export default CompanyQueriesForm;
